import * as turf from '@turf/turf'

type FullEditPaddockFeature = {
  properties: { id?: string; [key: string]: unknown }
  geometry?: turf.Geometry
}

type SelectedFarmSubdivisionForSave = { paddockIds?: string[] } | null | undefined

// Mirrors the exact partition AllotmentPanel's updateAllotment already performs (paddocks kept
// with an id vs. dropped vs. brand new) so the full-edit pre-flight lock check evaluates precisely
// what the save is about to touch, instead of a looser approximation.
//
// A locked paddock can never be edited through this UI — its row renders read-only with no
// rename/delete affordance — so any locked id still present in paddocksFeatures is guaranteed
// unchanged. Excluding it from the update batch (while retaining its id below) lets a loteo that
// contains one locked paddock still save unrelated edits, instead of every full-edit save being
// permanently blocked the moment any locked paddock exists in the year.
export const partitionPaddocksForFullEditSave = ({
  selectedFarmSubdivision,
  paddocksFeatures,
  lockedPaddockIds = [],
}: {
  selectedFarmSubdivision: SelectedFarmSubdivisionForSave
  paddocksFeatures: FullEditPaddockFeature[]
  lockedPaddockIds?: string[]
}) => {
  const currentFeatureIds = new Set(
    paddocksFeatures.map(paddockFeature => paddockFeature.properties.id).filter(Boolean),
  )
  // Lock-derived ids arrive lowercase (the lookup mirrors the backend's folding), while paddock and
  // subdivision rows can carry legacy casings — every lock membership check folds both sides, and
  // retained ids keep the row's own casing so the persisted paddockIds value is not churned.
  const lockedIdSet = new Set(lockedPaddockIds.map(paddockId => paddockId.toLowerCase()))
  const existingPaddockIds = selectedFarmSubdivision?.paddockIds ?? []

  const paddockIdsToDelete = existingPaddockIds.filter(
    paddockId => !currentFeatureIds.has(paddockId),
  )
  // Still part of the loteo and locked, so it must stay in paddockIds without being re-sent.
  const retainedLockedPaddockIds = existingPaddockIds.filter(
    paddockId => lockedIdSet.has(paddockId.toLowerCase()) && currentFeatureIds.has(paddockId),
  )
  const paddocksFeaturesToUpdate = paddocksFeatures.filter(
    paddock => paddock.properties.id && !lockedIdSet.has(paddock.properties.id.toLowerCase()),
  )
  const paddocksFeaturesToCreate = paddocksFeatures.filter(paddock => !paddock.properties.id)

  return {
    paddockIdsToDelete,
    paddocksFeaturesToUpdate,
    paddocksFeaturesToCreate,
    retainedLockedPaddockIds,
  }
}

// Server-Side Lock Enforcement (spec: paddock-metric-lock) rejects deleting a locked paddock.
// partitionPaddocksForFullEditSave already excludes locked ids from the update batch, so a
// present-and-unmodified locked paddock is safe by construction — but "present" is not the same
// as "unmodified". checkIntersectionWithOtherFeatures (mapDrawHandle.js) clips whatever EXISTING
// polygon a newly drawn/edited one overlaps and carries its id over unchanged, so a locked
// paddock's geometry can be silently rewritten in paddocksFeatures with zero row interaction.
//
// geometrySnapshot MUST be captured post-normalization (after the load-pass perimeter/neighbor
// intersect already ran), not the raw fetched geometry — that pass legitimately reshapes ANY
// paddock whose stored geometry crosses the perimeter or overlaps a neighbor, with zero user
// action, which would otherwise read as a false "touched" and permanently block the save. The
// backend enforces locks authoritatively at every mutating entry point regardless: this check only
// decides when the frontend UX guard should interrupt, so comparing against what the user's
// session actually started from is sufficient — no need for anything more elaborate.
export const findLockedPaddocksTouchedByFullEditSave = ({
  paddockIdsToDelete,
  paddocksFeatures = [],
  geometrySnapshot = {},
  lockedPaddockIds,
}: {
  paddockIdsToDelete: string[]
  paddocksFeatures?: FullEditPaddockFeature[]
  geometrySnapshot?: Record<string, turf.Geometry>
  lockedPaddockIds: string[]
}): string[] => {
  if (!lockedPaddockIds?.length) return []

  // Same folding contract as partitionPaddocksForFullEditSave: locked ids are lowercase, the ids
  // they are checked against may not be.
  const lockedIds = lockedPaddockIds.map(id => id.toLowerCase())
  const deletedIds = new Set(paddockIdsToDelete.map(id => id.toLowerCase()))

  const deletedTouched = lockedIds.filter(id => deletedIds.has(id))

  const geometryTouched = lockedIds.filter(id => {
    if (deletedTouched.includes(id)) return false
    const currentFeature = paddocksFeatures.find(
      paddock => paddock.properties.id?.toLowerCase() === id,
    )
    // The snapshot is keyed by the feature's own (raw-cased) id, so the lookup must go through the
    // matched feature — indexing by the folded lock id would miss the baseline it was built from.
    const snapshotGeometry = currentFeature?.properties.id
      ? geometrySnapshot[currentFeature.properties.id]
      : undefined
    if (!currentFeature?.geometry || !snapshotGeometry) return false
    try {
      // booleanEqual (topology-aware, tolerant of float noise) rather than a byte-for-byte
      // comparison — re-renders can shift trailing coordinate precision with no real change.
      return !turf.booleanEqual(
        turf.feature(currentFeature.geometry),
        turf.feature(snapshotGeometry),
      )
    } catch {
      // Geometry too malformed to compare — fail closed (treat as touched) rather than silently
      // letting an unverifiable mismatch through.
      return true
    }
  })

  return [...deletedTouched, ...geometryTouched]
}

// Captured on the "Editar Loteo" click — after the load-pass normalization already ran, before any
// user edit — which is exactly the timing the post-normalization contract above requires.
// Geometries are shared by reference, not cloned: the panel replaces feature objects immutably on
// every edit, so a snapshot entry can never be mutated behind the guard's back. Id-less features
// are unsaved drawings with no lock baseline to compare against.
export const capturePaddockGeometrySnapshot = (
  paddocksFeatures: FullEditPaddockFeature[],
): Record<string, turf.Geometry> =>
  Object.fromEntries(
    paddocksFeatures
      .filter(paddock => paddock.properties.id)
      .map(paddock => [paddock.properties.id, paddock.geometry]),
  )
